import { createPart, type PagePart, type PartType } from '@/features/storefront/sections/parts';
import { defaultSection } from '@/features/storefront/sections/pages';
import { createSection, type Section } from '@/features/storefront/sections/schema';

/**
 * Emeraude's product page, written down.
 *
 * A jeweller's product page: the piece photographed large on the left, and on
 * the right the maker's name in small capitals, the name in the serif, the
 * price with what it was and the saving, the finish and size as pills, how
 * many, and two ways out - add to cart, or buy it now - with the folded
 * panels (description, specifications, delivery) under them in the same
 * column. Under both columns, the related pieces, then the reviews.
 *
 * As with Hyper's and Lumera's, these are *defaults*: editable, movable and
 * removable in the customiser, and carrying nothing that belongs to the
 * sample shop - no photographs, no quotes, no offer, no promise the merchant
 * never made. The delivery panel draws nothing until the merchant writes it.
 */

/**
 * One component of the page, with an id that does not change between renders
 * - see Hyper's `productPage.ts` for why a default must not mint a fresh id.
 */
function part(
  id: string,
  type: PartType,
  region: string,
  over: Partial<Omit<PagePart, 'id' | 'type'>> = {},
): PagePart {
  const base = createPart(type, region);
  return {
    ...base,
    ...over,
    id: `emeraude-${id}`,
    region,
    options: { ...base.options, ...(over.options ?? {}) },
  };
}

/** The components of the product, in the regions `Product.tsx` lays out. */
export function emeraudeProductParts(): PagePart[] {
  return [
    part('breadcrumbs', 'product_breadcrumbs', 'top'),

    part('gallery', 'product_gallery', 'gallery'),

    part('brand', 'product_brand', 'main'),
    part('title', 'product_title', 'main'),
    part('price', 'product_price', 'main', {
      options: { show_compare_at: true, show_saving: true },
    }),
    part('summary', 'product_short_description', 'main'),
    part('variants', 'product_variants', 'main'),
    part('quantity', 'product_quantity', 'main'),
    part('add', 'product_add_to_cart', 'main', { width: 'full' }),
    part('buy', 'product_buy_now', 'main', { width: 'full', options: { style: 'solid' } }),
    part('share', 'product_share', 'main'),

    part('description', 'product_description', 'main', {
      label: 'Description',
      space_above: 'lg',
      options: { show_heading: true, collapsible: true },
    }),
    part('specs', 'product_specs', 'main', {
      label: 'Specifications',
      options: { collapsible: true },
    }),
    part('delivery', 'product_shipping_info', 'main', {
      label: 'Shipping and returns',
      body: '',
      options: { collapsible: true, icon: 'truck' },
    }),
    part('related', 'product_related', 'below'),
  ];
}

/** A section with a stable id, for the same reason {@link part} fixes one. */
function fixed(id: string, section: Section): Section {
  return { ...section, id: `emeraude-${id}` };
}

/** The product page as a merchant on this theme first finds it. */
export function emeraudeProductSections(): Section[] {
  return [
    defaultSection('product_details'),
    fixed('reviews', {
      ...createSection('product_reviews'),
      title: 'Customer reviews',
      background: 'surface',
      padding: 'lg',
    }),
  ];
}
